
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../components/ui/Logo';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { setUser, setAuthTokens } from '../lib/storage';
import Modal from '../components/ui/Modal';

interface SignupPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  username: string;
  department?: 'RCS' | 'ADAM';
}

const Signup: React.FC = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();

  const [form, setForm] = useState<SignupPayload>({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    username: '',
  });

  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPostSignupModal, setShowPostSignupModal] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [modalDepartment, setModalDepartment] = useState<'RCS' | 'ADAM' | ''>('');
  const [signupResponse, setSignupResponse] = useState<any>(null);

  function update<K extends keyof SignupPayload>(key: K, value: SignupPayload[K]) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const res: any = await signup(form);

      setSignupResponse(res);

      // setShowPostSignupModal(true);

      toast.success(res?.message || 'Account created. Please complete organization details.');
    } catch (err: any) {
      console.log('Signup error', err);
      const msg = err?.message || 'Failed to create account';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalSubmit = async () => {
    if (!orgName.trim()) {
      toast.error('Organization is required');
      return;
    }
    if (!modalDepartment) {
      toast.error('Please select a department');
      return;
    }

    try {
      if (signupResponse?.user) {
        setUser(signupResponse.user);
      }

      if (signupResponse?.token) {
        setAuthTokens(signupResponse.token);
      } else if (signupResponse?.accessToken) {
        setAuthTokens(signupResponse.accessToken);
      }



      toast.success('Organization and department saved — all set!');
      setShowPostSignupModal(false);
      navigate('/login');
    } catch (err: any) {
      console.error('Failed to finalize profile', err);
      toast.error(err?.message || 'Failed to save organization details');
    }
  };

  return (
    <div className="min-h-screen bg-agentic-white-off">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* Left Panel - Brand + Metrics (same as you had) */}
        <div className="hidden lg:flex relative p-8">
          <div className="relative w-full rounded-3xl overflow-hidden bg-gradient-to-br from-blue-200 via-blue-300 to-blue-400">
            <div className="absolute inset-0 opacity-70">
              <div className="absolute -top-10 -left-10 w-80 h-80 bg-white/30 rounded-full blur-3xl"></div>
              <div className="absolute bottom-10 right-10 w-96 h-96 bg-white/20 rounded-full blur-3xl"></div>
            </div>

            <div className="relative p-10 space-y-8">
              <div className="max-w-xl">
                <h2 className="text-3xl font-semibold text-gray-800 leading-tight">
                  Orchestrate your Agentic AI workforce
                </h2>
                <p className="mt-3 text-gray-700">
                  Coordinate autonomous agents across claims, underwriting, fraud, and support — securely, at enterprise scale.
                </p>
              </div>

              <div className="space-y-6 max-w-md">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-md">
                  <div className="text-xs font-semibold text-gray-500 tracking-wide">AGENTS ONLINE</div>
                  <div className="mt-2 flex items-end space-x-2">
                    <div className="text-4xl font-bold text-gray-900">18</div>
                    <div className="text-xs text-gray-500 mb-2">live</div>
                  </div>
                  <p className="mt-2 text-sm text-gray-600">
                    Health across OCR, Customer Support, Compliance, Fraud, and Reporting agents
                  </p>
                  <div className="mt-4 flex items-end space-x-2 h-24">
                    <div className="w-6 bg-green-200 h-6 rounded"></div>
                    <div className="w-6 bg-green-300 h-10 rounded"></div>
                    <div className="w-6 bg-green-400 h-12 rounded"></div>
                    <div className="w-6 bg-green-500 h-20 rounded"></div>
                  </div>
                </div>

                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-md">
                  <div className="text-xs font-semibold text-gray-500 tracking-wide">AUTOMATIONS EXECUTED</div>
                  <div className="mt-2 flex items-end space-x-2">
                    <div className="text-4xl font-bold text-gray-900">26.8K</div>
                    <div className="text-xs text-gray-500 mb-2">last month</div>
                  </div>
                  <p className="mt-2 text-sm text-gray-600">
                    Automated triage, enrichment, and decisioning across enterprise workflows
                  </p>
                  <div className="mt-4 flex items-end space-x-2 h-24">
                    <div className="w-6 bg-blue-200 h-10 rounded"></div>
                    <div className="w-6 bg-blue-300 h-16 rounded"></div>
                    <div className="w-6 bg-blue-400 h-20 rounded"></div>
                    <div className="w-6 bg-blue-500 h-24 rounded"></div>
                  </div>
                </div>
              </div>

              <div className="max-w-md mt-6">
                <div className="bg-white/70 rounded-2xl p-6 shadow-inner">
                  <svg className="w-full h-40" viewBox="0 0 400 160">
                    <defs>
                      <linearGradient id="node" x1="0" x2="1">
                        <stop offset="0%" stopColor="#93c5fd" />
                        <stop offset="100%" stopColor="#3b82f6" />
                      </linearGradient>
                    </defs>
                    <g stroke="#93c5fd" strokeWidth="2" opacity="0.8">
                      <line x1="60" y1="80" x2="160" y2="40" />
                      <line x1="60" y1="80" x2="160" y2="120" />
                      <line x1="160" y1="40" x2="300" y2="80" />
                      <line x1="160" y1="120" x2="300" y2="80" />
                    </g>
                    <g>
                      <circle cx="60" cy="80" r="12" fill="url(#node)" />
                      <rect x="152" y="32" width="16" height="16" rx="3" fill="url(#node)" />
                      <rect x="152" y="112" width="16" height="16" rx="3" fill="url(#node)" />
                      <circle cx="300" cy="80" r="12" fill="url(#node)" />
                    </g>
                  </svg>
                  <div className="text-xs text-gray-600 text-center">Autonomous agents collaborating across your enterprise graph</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Signup Form */}
        <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
          <div className="w-full max-w-md">
            <div className="flex items-center mb-6">
              <img
                src={process.env.REACT_APP_APPLICATION_LOGO_URL || "agentic-logo.png"}
                alt={process.env.REACT_APP_APPLICATION_NAME || 'Agentic Platform'}
                className="h-16 w-auto"
                loading="eager"
                referrerPolicy="no-referrer"
              />
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-1">Create your Agentix account</h1>
            <p className="text-gray-600 mb-8">Sign up to get started — basic account details first.</p>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <label className="flex flex-col text-sm">
                  <span className="text-gray-600 text-xs font-medium">First name</span>
                  <input
                    required
                    value={form.firstName}
                    onChange={e => update('firstName', e.target.value)}
                    className="mt-1 p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-200"
                    placeholder="John"
                  />
                </label>

                <label className="flex flex-col text-sm">
                  <span className="text-gray-600 text-xs font-medium">Last name</span>
                  <input
                    required
                    value={form.lastName}
                    onChange={e => update('lastName', e.target.value)}
                    className="mt-1 p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-200"
                    placeholder="Doe"
                  />
                </label>
              </div>

              <label className="flex flex-col text-sm">
                <span className="text-gray-600 text-xs font-medium">Username</span>
                <input
                  required
                  value={form.username}
                  onChange={e => update('username', e.target.value)}
                  className="mt-1 p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-200"
                  placeholder="johndoe"
                />
              </label>

              <label className="flex flex-col text-sm">
                <span className="text-gray-600 text-xs font-medium">Email</span>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={e => update('email', e.target.value)}
                  className="mt-1 p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-200"
                  placeholder="you@company.com"
                />
              </label>

              <div className="flex items-center gap-3">
                <label className="flex-1 flex flex-col text-sm">
                  <span className="text-gray-600 text-xs font-medium">Password</span>
                  <div className="relative mt-1">
                    <input
                      required
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={e => update('password', e.target.value)}
                      className="w-full p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-200"
                      placeholder="Enter a strong password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(s => !s)}
                      className="absolute right-2 top-2 text-xs text-gray-500 px-2 py-1"
                      aria-label="Toggle password visibility"
                    >
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </label>
              </div>

              {/* Role selector moved to modal - show a note here */}
              {/* <div className="text-sm">
                <div className="text-xs text-gray-500 font-semibold mb-2">Choose role</div>
                <div className="text-xs text-gray-500 mb-2">(You will choose department after creating your account)</div>
              </div> */}

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 rounded-lg bg-agentic-primary-blue text-white font-semibold disabled:opacity-60"
                >
                  {submitting ? 'Creating account...' : 'Create account'}
                </button>
              </div>

              <div className="text-center text-sm text-gray-500">
                By creating an account you agree to our <span className="underline">Terms</span> and <span className="underline">Privacy Policy</span>.
              </div>
            </form>

            <div className="mt-6 text-center text-sm text-gray-500">
              Already have an account? <button onClick={() => navigate('/login')} className="text-agentic-primary-blue hover:text-agentic-primary-blue-dark font-medium">Sign in</button>
            </div>

            <div className="mt-6 text-center text-xs text-gray-400">© 2026 {process.env.REACT_APP_APPLICATION_NAME || 'Agentic Platform'}, Inc. All rights reserved. END-TO-END CONVERGENCE</div>
          </div>
        </div>
      </div>

      {/* Post-signup modal */}
      <Modal
        isOpen={showPostSignupModal}
        onClose={() => {
          return;
        }}
        title="Complete Organization Details"
        size="md"
        showCloseButton={false}
        closeOnBackdropClick={false}
        closeOnEsc={false}
      >
        <div>
          <p className="text-sm text-gray-600 mb-4">Please provide your organization and select your department to finish setup.</p>

          <label className="flex flex-col text-sm mb-3">
            <span className="text-gray-600 text-xs font-medium">Organization</span>
            <input
              value={orgName}
              onChange={e => setOrgName(e.target.value)}
              className="mt-1 p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-200"
              placeholder="Your organization name"
            />
          </label>

          <label className="flex flex-col text-sm mb-6">
            <span className="text-gray-600 text-xs font-medium">Department</span>
            <select
              value={modalDepartment}
              onChange={e => setModalDepartment(e.target.value as 'RCS' | 'ADAM' | '')}
              className="mt-1 p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-200"
            >
              <option value="">Select department</option>
              <option value="RCS">RCS — Agent orchestration & routing</option>
              <option value="ADAM">ADAM — Autonomous decision automation</option>
            </select>
          </label>

          <div className="flex justify-end gap-3">
            <button
              onClick={handleModalSubmit}
              className="px-4 py-2 rounded-lg bg-agentic-primary-blue text-white font-semibold"
            >
              Save & Continue
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Signup;
